import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { Box, Button, Typography } from "@mui/material";
import { useState } from "react";

import { useActorPriceCategoriesQuery } from "Actors/Api/Queries/useActorPriceCategoriesQuery";
import { ActorPriceCategoryStatus } from "Infrastructure/Api/Api";
import {
  getTranslation,
  Resources,
  useResource,
} from "Infrastructure/Translations/Resources";
import { EditIcon } from "Shared/Icons";
import {
  type BlDetailSectionItem,
  DetailSection,
} from "Shared/Sections/DetailSection";
import { EditActorPriceCategoriesModal } from "./EditActorPriceCategoriesModal";

interface ActorPriceCategoriesSectionProps {
  actorID: string;
}

function getPriceSuffix(type: string): string {
  return type === "PerMinute"
    ? getTranslation(Resources.PriceCategories.PriceSuffix.PerMinute)
    : getTranslation(Resources.PriceCategories.PriceSuffix.PerProject);
}

export const ActorPriceCategoriesSection = ({
  actorID,
}: ActorPriceCategoriesSectionProps): React.ReactNode => {
  const { t } = useResource();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: actorCategories, isLoading } =
    useActorPriceCategoriesQuery(actorID);

  const visibleRates = (actorCategories ?? []).filter(
    (c) =>
      c.status === ActorPriceCategoryStatus.Allowed ||
      c.status === ActorPriceCategoryStatus.Exclusive,
  );

  const items: BlDetailSectionItem[] = visibleRates.map((rate) => ({
    id: rate.priceCategoryID,
    startAdornment: (
      <DetailSection.IconAdornment>
        <RecordVoiceOverIcon />
      </DetailSection.IconAdornment>
    ),
    label: { text: rate.priceCategoryName },
    endAdornment: (
      <Box
        sx={{
          fontSize: 14,
          whiteSpace: "nowrap",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          component="span"
          sx={{
            lineHeight: 1,
          }}
        >
          {rate.price}
        </Typography>
        <Typography
          component="span"
          variant="caption"
          sx={{ color: "text.secondary", ml: 0.5, mt: 0.75 }}
        >
          {getPriceSuffix(rate.priceCategoryType)}
        </Typography>
      </Box>
    ),
  }));

  return (
    <>
      <DetailSection
        title={t(Resources.Actors.ActorDetail.VoiceModuleRates)}
        action={
          <Button
            variant="text"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => setIsEditModalOpen(true)}
            sx={{ textTransform: "none" }}
          >
            {t(Resources.Actors.ActorDetail.UpdateRates)}
          </Button>
        }
        items={items}
        isLoading={isLoading}
        emptyMessage={t(Resources.Actors.ActorDetail.NoRatesConfigured)}
      />

      <EditActorPriceCategoriesModal
        actorID={actorID}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />
    </>
  );
};
